// Cancellation friction flow.
//
// Patients used to cancel via Stripe's Customer Portal — one click, no
// reason captured, no retention attempt. This modal replaces that with:
//
//   1. Reason picker (effectiveness / side effects / frequency-leftover
//      / expense / different source / other)
//   2. Reason-specific routing:
//        effectiveness, side_effects → "talk to a clinician" panel
//          with a deep link to /messages (pre-fills a draft)
//        frequency                   → "pause instead" panel pointing
//          to the care team (since we don't have an in-app pause yet)
//        expense                     → straight to retention offer
//        different_source, other     → "are you sure?" panel with the
//          adjustment-period disclaimer, then retention offer
//   3. Retention offer: 10% off the next 3 months. Eligibility check
//      happens server-side (max 2 lifetime per patient).
//   4. Confirmation step with effective date.
//
// All Stripe writes happen in the patient-cancel-subscription edge
// function (not from the browser).

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/Button";
import { Alert } from "@/components/Alert";

type Reason =
  | "effectiveness"
  | "side_effects"
  | "frequency"
  | "expense"
  | "different_source"
  | "other";

type Step =
  | "reason"
  | "route_provider"
  | "route_pause"
  | "are_you_sure"
  | "retention_offer"
  | "confirm"
  | "done";

interface Props {
  open: boolean;
  onClose: () => void;
  treatmentId: string;
  medicationLabel: string;
  nextChargeDate: string | null;
  onCancelled?: () => void; // parent can refetch treatments
}

const REASONS: Array<{ id: Reason; label: string; sub: string }> = [
  { id: "effectiveness",    label: "It's not working for me",                sub: "Not seeing the results I expected" },
  { id: "side_effects",     label: "Side effects",                           sub: "Symptoms I want to talk through" },
  { id: "frequency",        label: "I have leftover medication",             sub: "Using less than I get / want to space out shipments" },
  { id: "expense",          label: "Too expensive",                          sub: "Cost is the main reason" },
  { id: "different_source", label: "Going somewhere else",                   sub: "Found another provider or source" },
  { id: "other",            label: "Other",                                  sub: "Different reason" },
];

export function CancelSubscriptionModal({
  open, onClose, treatmentId, medicationLabel, nextChargeDate, onCancelled,
}: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("reason");
  const [reason, setReason] = useState<Reason | null>(null);
  const [reasonNotes, setReasonNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [effectiveDate, setEffectiveDate] = useState<string | null>(null);

  // Reset whenever the modal opens for a new treatment.
  useEffect(() => {
    if (open) {
      setStep("reason");
      setReason(null);
      setReasonNotes("");
      setError(null);
      setEffectiveDate(null);
    }
  }, [open, treatmentId]);

  if (!open) return null;

  async function quoteRetention(r: Reason): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke<{
        eligible_for_retention?: boolean; error?: string;
      }>("patient-cancel-subscription", {
        body: { action: "quote", treatment_id: treatmentId, reason: r },
      });
      if (fnErr) throw new Error(fnErr.message ?? "Failed to load offer eligibility");
      if (data?.error) throw new Error(data.error);
      return !!data?.eligible_for_retention;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function pickReason(r: Reason) {
    setReason(r);
    if (r === "effectiveness" || r === "side_effects") {
      setStep("route_provider");
      // We still log this as an outcome so the funnel shows what happens
      // (silently fire-and-forget on the cancel endpoint with outcome=routed_to_provider).
      void logExit(r, "routed_to_provider");
      return;
    }
    if (r === "frequency") {
      setStep("route_pause");
      void logExit(r, "routed_to_pause");
      return;
    }
    if (r === "expense") {
      // Skip "are you sure" — offer the coupon immediately.
      const eligible = await quoteRetention(r);
      setStep(eligible ? "retention_offer" : "confirm");
      return;
    }
    // different_source, other → soft "are you sure" first.
    setStep("are_you_sure");
  }

  async function logExit(r: Reason, outcome: "routed_to_provider" | "routed_to_pause") {
    try {
      await supabase.functions.invoke("patient-cancel-subscription", {
        body: { action: "cancel", treatment_id: treatmentId, reason: r, outcome },
      });
    } catch { /* best-effort */ }
  }

  async function applyRetention() {
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke<{
        ok?: boolean; coupon_applied?: string; error?: string;
      }>("patient-cancel-subscription", {
        body: {
          action: "apply_retention",
          treatment_id: treatmentId,
          reason,
          reason_notes: reasonNotes.trim() || null,
        },
      });
      if (fnErr) throw new Error(fnErr.message ?? "Couldn't apply offer");
      if (data?.error) throw new Error(data.error);
      setStep("done");
      onCancelled?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function proceedToCancel(outcome: "cancelled" | "retention_declined") {
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke<{
        ok?: boolean; effective_date?: string; error?: string;
      }>("patient-cancel-subscription", {
        body: {
          action: "cancel",
          treatment_id: treatmentId,
          reason,
          reason_notes: reasonNotes.trim() || null,
          outcome,
        },
      });
      if (fnErr) throw new Error(fnErr.message ?? "Couldn't cancel");
      if (data?.error) throw new Error(data.error);
      setEffectiveDate(data?.effective_date ?? null);
      setStep("done");
      onCancelled?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 23, 18, 0.55)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[18px] shadow-2xl max-w-[520px] w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 pt-6 pb-3 flex items-start justify-between gap-4 border-b border-fh-border">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-fh-gray">Cancel subscription</div>
            <h2 id="cancel-modal-title" className="font-serif text-[22px] text-fh-ink mt-1 mb-0">
              {medicationLabel}
            </h2>
            {nextChargeDate && (
              <p className="text-[12.5px] text-fh-gray mt-1 mb-0">
                Next charge currently scheduled for {nextChargeDate}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-fh-gray hover:text-fh-ink text-[22px] leading-none -mt-1"
          >×</button>
        </div>

        <div className="px-6 py-5">
          {error && <div className="mb-4"><Alert tone="error">{error}</Alert></div>}

          {step === "reason" && (
            <>
              <h3 className="text-[15px] font-semibold text-fh-ink mb-1">
                Before we cancel — what's the main reason?
              </h3>
              <p className="text-[13px] text-fh-gray mb-4">
                Your clinician sees this so we can improve. Pick the closest fit.
              </p>
              <div className="flex flex-col gap-2">
                {REASONS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    disabled={busy}
                    onClick={() => pickReason(r.id)}
                    className="text-left w-full px-4 py-3 rounded-[10px] border border-fh-border hover:border-fh-green-dark bg-white transition"
                  >
                    <div className="font-semibold text-[14px] text-fh-ink">{r.label}</div>
                    <div className="text-[12px] text-fh-gray mt-0.5">{r.sub}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === "route_provider" && (
            <RoutingPanel
              title={reason === "side_effects"
                ? "Side effects deserve a clinician's eyes"
                : "Let's talk to a clinician first"}
              body={reason === "side_effects"
                ? "Most side effects are manageable with a dose adjustment or formulation change. Send your care team a message — they can usually sort it out same-day."
                : "Effectiveness varies a lot by dose, timing, and adherence. Your clinician can adjust your protocol before you give up on the medication."}
              primary={{
                label: "Message care team",
                onClick: () => {
                  onClose();
                  navigate("/messages");
                },
              }}
              secondary={{ label: "Continue cancelling anyway", onClick: () => setStep("are_you_sure") }}
              closeLabel="Never mind, keep subscription"
              onClose={onClose}
            />
          )}

          {step === "route_pause" && (
            <RoutingPanel
              title="Pause instead of cancel?"
              body="If you have medication left over and just want to space out your shipments, you can pause your subscription. Message your care team and they'll pause it for you — no need to cancel and re-enroll later."
              primary={{
                label: "Message care team to pause",
                onClick: () => {
                  onClose();
                  navigate("/messages");
                },
              }}
              secondary={{ label: "I'd rather cancel", onClick: () => setStep("are_you_sure") }}
              closeLabel="Never mind, keep subscription"
              onClose={onClose}
            />
          )}

          {step === "are_you_sure" && (
            <>
              <h3 className="text-[15px] font-semibold text-fh-ink mb-2">
                Are you sure?
              </h3>
              <div className="rounded-[10px] bg-fh-sand p-3 text-[13px] text-fh-ink leading-relaxed mb-4">
                Studies have shown that {medicationLabel.toLowerCase().includes("testosterone")
                  ? "testosterone therapy"
                  : "GLP-1 medications like this one"}{" "}
                can take <strong>3–6 months of consistent use</strong> before you
                see the full benefit. Stopping early is the most common reason
                patients don't get the results they wanted.
              </div>
              <label className="block text-[12.5px] font-semibold text-fh-gray mb-1">
                Anything you'd like your care team to know? (Optional)
              </label>
              <textarea
                rows={3}
                value={reasonNotes}
                onChange={(e) => setReasonNotes(e.target.value)}
                placeholder="Optional — helps us understand and improve."
                className="w-full px-3 py-2 text-[13.5px] border border-fh-border rounded-[8px] focus:outline-none focus:ring-2 focus:ring-fh-green focus:border-fh-green"
              />
              <div className="mt-4 flex flex-wrap gap-2 justify-end">
                <Button variant="ghost" onClick={onClose} disabled={busy}>
                  Keep my subscription
                </Button>
                <Button
                  loading={busy}
                  onClick={async () => {
                    if (!reason) return;
                    const eligible = await quoteRetention(reason);
                    setStep(eligible ? "retention_offer" : "confirm");
                  }}
                >
                  Continue
                </Button>
              </div>
            </>
          )}

          {step === "retention_offer" && (
            <>
              <h3 className="text-[15px] font-semibold text-fh-ink mb-2">
                Would <span className="text-fh-green-dark">10% off the next 3 months</span> change your mind?
              </h3>
              <p className="text-[13px] text-fh-gray mb-4 leading-relaxed">
                We'd hate to lose you over price. We can apply a 10% discount
                to your next 3 invoices — the coupon attaches automatically and
                you don't have to do anything else.
              </p>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="ghost" onClick={() => proceedToCancel("retention_declined")} disabled={busy}>
                  No thanks, cancel anyway
                </Button>
                <Button loading={busy} onClick={applyRetention}>
                  Apply 10% off — keep subscription
                </Button>
              </div>
            </>
          )}

          {step === "confirm" && (
            <>
              <h3 className="text-[15px] font-semibold text-fh-ink mb-2">
                Confirm cancellation
              </h3>
              <p className="text-[13px] text-fh-ink leading-relaxed mb-4">
                Your subscription will <strong>stay active until your current billing
                cycle ends</strong>{nextChargeDate ? ` (${nextChargeDate})` : ""}. After that,
                no further charges and no further shipments. You can re-enroll
                any time.
              </p>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="ghost" onClick={onClose} disabled={busy}>
                  Never mind
                </Button>
                <Button loading={busy} onClick={() => proceedToCancel("cancelled")}>
                  Cancel subscription
                </Button>
              </div>
            </>
          )}

          {step === "done" && (
            <>
              <h3 className="text-[15px] font-semibold text-fh-ink mb-2">
                {effectiveDate === null ? "Discount applied" : "Cancellation scheduled"}
              </h3>
              <p className="text-[13px] text-fh-ink leading-relaxed mb-4">
                {effectiveDate === null ? (
                  <>10% off has been applied to your next 3 invoices. Nothing else changes —
                  your subscription continues on schedule.</>
                ) : (
                  <>
                    Your subscription will end on{" "}
                    <strong>{new Date(effectiveDate).toLocaleDateString(undefined, {
                      year: "numeric", month: "long", day: "numeric",
                    })}</strong>. We've emailed you a confirmation. You can re-enroll
                    any time.
                  </>
                )}
              </p>
              <div className="flex justify-end">
                <Button onClick={onClose}>Close</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RoutingPanel({
  title, body, primary, secondary, closeLabel, onClose,
}: {
  title: string;
  body: string;
  primary: { label: string; onClick: () => void };
  secondary: { label: string; onClick: () => void };
  closeLabel: string;
  onClose: () => void;
}) {
  return (
    <>
      <h3 className="text-[15px] font-semibold text-fh-ink mb-2">{title}</h3>
      <p className="text-[13px] text-fh-ink leading-relaxed mb-4">{body}</p>
      <div className="flex flex-col gap-2">
        <Button onClick={primary.onClick}>{primary.label}</Button>
        <Button variant="ghost" onClick={onClose}>{closeLabel}</Button>
        <button
          type="button"
          onClick={secondary.onClick}
          className="text-[12.5px] text-fh-gray hover:text-fh-ink underline mt-1"
        >
          {secondary.label}
        </button>
      </div>
    </>
  );
}
